import PropTypes from 'prop-types';
import React, { Children, cloneElement, Component } from 'react';
import isEmpty from 'lodash/isEmpty';
import MessageBox from 'yoda-core-components/lib/components/MessageBox/MessageBox';
import classNames from 'classnames/bind';
import * as styles from './CheckoutAccordion.css';

const cx = classNames.bind(styles);
/**
 * Accordion component to display any HTML elements or Components within it,a wrapper compoenent for AccordionSection.
 * Currently it supports default opening  of AccordionSection, Toggling same pane,Toggling different panes . Also support
 * theming
 *
 * Many components could be of Accordion view. E.g Filter options for Filtering search results, Footer contents
 * Product summary page with Review,Product detail  etc in Accordion view
 */

class CheckoutAccordion extends Component {
    /**
     * accordionInstanceCount
     * Keeps track of number of instances
     * of the component
     * @type {number}
     */
    static accordionInstanceCount = 0;

    /**
     * Supported React properties
     * @type {Object}
     */
    static propTypes = {
        /**
         * Default selectedAccordionSectionIndex
         * selectedAccordionSectionIndex will automatically open
         * if not given then all panes will be closed
         * @type {number}
         */
        selectedAccordionSectionIndex: PropTypes.number,

        /**
         * isAutoCollapsible will open the selected pane
         * and auto closes other panes
         * i.e, only one pane will be open at a time
         * @type {boolean}
         */
        isAutoCollapsible: PropTypes.bool.isRequired,

        /**
         * themeConfig will help in applying a different theme
         * i.e, User can override the default theme by passing
         * themeConfig as props
         * @type {boolean}
         */
        themeConfig: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),

        /**
         * Children nodes of Accordion wrapper
         * @type {node}
         */
        children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),

        /**
         * toExpandAll will keep open all the tabs by default
         * it needs isAutoCollapsible false to work
         * @type {boolean}
         */

        /**
         * Unique name for referencing dom element in automation testing
         * @type {String}
         */

        automationId: PropTypes.string,
        changeAccordion: PropTypes.func.isRequired,
        setNextAccordionIndex: PropTypes.func.isRequired,
        createAccountMessage: PropTypes.string,

        /**
         * Checking whether the isAutoCollapsible and toExpandAll set to true
         */
        toExpandAll(props, propName, componentName) {
            let error;

            if (props.isAutoCollapsible && props.toExpandAll) {
                error = new Error(
                    `validation failed: invalid prop '${propName}' supplied to '${componentName}'.`
                );
            }

            return error;
        },
    };

    /** @properties {Default set up} [optonal props set] */
    static defaultProps = {
        selectedAccordionSectionIndex: -1,
        toExpandAll: false,
        themeConfig: [],
        children: '',
        automationId: '',
        createAccountMessage: '',
    };

    /**
     * Gets the theme to use
     * @return {[Object]} [theming classes]
     */
    static getThemeConfig(themeConfig) {
        return {
            accordionContainerClass: styles.accordion,
            accordionSectionClass: styles.accordionSection,
            accordionActiveClass: styles.selected,
            accordionTitleClass: styles.panelHeader,
            accordionPanelClass: styles.panel,
            accordionIconClass: styles.iconBlock,
            ...themeConfig,
        };
    }

    constructor(props) {
        super(props);
        /* istanbul ignore next */

        this.onAccordionSectionSelect = this.onAccordionSectionSelect.bind(this);
        this.themeConfig = CheckoutAccordion.getThemeConfig(props.themeConfig);
        /**
         * Generating Uniqueid for identifiying each accordion for Accessibility
         * Utils function will be used here
         */
        CheckoutAccordion.accordionInstanceCount += 1;
        this.accordionKey = CheckoutAccordion.accordionInstanceCount;
    }

    /**
     * @callback function for the onSelect props
     * updating the selectedAccordionSectionIndex Pane and selectedAccordionSectionIndex state
     */
    onAccordionSectionSelect(selectedAccordionSectionIndex, nextAccordionIndex) {
        /**
         * Set the selectedAccordionSectionIndex in here
         * if the same accordion section is clicked again setting it to -1 to close it.
         */
        this.props.changeAccordion(selectedAccordionSectionIndex);
        this.props.setNextAccordionIndex(
            nextAccordionIndex !== undefined
                ? nextAccordionIndex
                : selectedAccordionSectionIndex + 1
        );
    }

    /**
     * Cloned child with props
     * @return {[cloned child Object]} [updated props on child]
     */
    getAccordionSectionConfig(accordionSection) {
        if (accordionSection && accordionSection.props && accordionSection.props.index) {
            const accordionSectionIndex = accordionSection.props.index;
            return cloneElement(accordionSection, {
                key: accordionSectionIndex,
                isSelected: accordionSectionIndex === this.props.selectedAccordionSectionIndex,
                isAutoCollapsible: this.props.isAutoCollapsible,
                onSelect: this.onAccordionSectionSelect,
                toExpandAll: this.props.toExpandAll,
                themeConfig: this.themeConfig,
                accordionKey: this.accordionKey,
            });
        }
        return null;
    }

    renderCreateAccountSuccessMessage() {
        if (isEmpty(this.props.createAccountMessage)) {
            return null;
        }
        return (
            <div>
                <MessageBox
                    type="success"
                    level="section"
                    title={this.props.createAccountMessage}
                    hasClose={false}
                    automationId="checkout-create-account-successMessage"
                />
            </div>
        );
    }

    /**
     * Renders all the children inside it
     * @return {[type]} [description]
     */
    render() {
        // Use Children to map and clone with the props and see how it goes in here
        const childAccordionSections = Children.map(
            this.props.children,
            this.getAccordionSectionConfig,
            this
        );
        const { automationId } = this.props;
        return (
            <div
                role="tablist"
                className={cx(this.themeConfig.accordionContainerClass, 'newFulfilmentOptions')}
                data-automation-id={automationId}
            >
                {this.renderCreateAccountSuccessMessage()}
                {childAccordionSections}
            </div>
        );
    }
}

export default CheckoutAccordion;
